import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Star, X, Loader2, CheckCircle, Play, Download, Upload, Link2, Music, Image, Eye, MessageSquare } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, inputNeed } from '../../utils/nodeRequirements';

const KlingAvatarNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [imageLocal, setImageLocal] = useState(null);
  const [audioLocal, setAudioLocal] = useState(null);
  const [mode, setMode] = useState(data.mode || 'pro');
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [localError, setLocalError] = useState(null);

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';
  const { openVideoPreview } = useMediaPreview();

  // Check for connected inputs (matching NODE_TYPE_CONFIG handle IDs)
  const imageEdge = edges.find((e) => e.target === id && e.targetHandle === 'inputImage');
  const audioEdge = edges.find((e) => e.target === id && e.targetHandle === 'inputAudio');

  const getConnectedNodeData = (edge) => {
    if (!edge) return null;
    const sourceNode = nodes.find((n) => n.id === edge.source);
    return sourceNode?.data;
  };

  const imageInputData = getConnectedNodeData(imageEdge);
  const audioInputData = getConnectedNodeData(audioEdge);

  const imageSource = imageInputData?.hasOutput ? {
    file: imageInputData.file,
    preview: imageInputData.outputUrl || imageInputData.preview,
    name: imageInputData.fileName || 'connected_image',
    fromConnection: true,
  } : imageLocal;

  const audioSource = audioInputData?.hasOutput ? {
    file: audioInputData.file,
    preview: audioInputData.outputUrl || audioInputData.preview,
    name: audioInputData.fileName || 'connected_audio',
    fromConnection: true,
  } : audioLocal;

  // Chronometer effect
  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleImageChange = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setImageLocal({ file, preview: e.target.result, name: file.name });
    reader.readAsDataURL(file);
  };

  const handleAudioChange = (file) => {
    if (!file) return;
    setAudioLocal({ file, preview: URL.createObjectURL(file), name: file.name });
  };

  const handleSourcePickImage = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) handleImageChange(result.file);
      else if (result.url) setImageLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, openSourcePicker]);

  const handleSourcePickAudio = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) handleAudioChange(result.file);
      else if (result.url) setAudioLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
    });
    openSourcePicker(id, ['audio'], 'audio/mp3,audio/wav,audio/m4a,audio/aac,audio/mpeg');
  }, [id, openSourcePicker]);

  const fetchFileFromUrl = async (url, filename, isAudio = false) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const type = isAudio ? (blob.type || 'audio/mp3') : (blob.type || 'image/jpeg');
      return new File([blob], filename, { type });
    } catch (error) {
      console.error('Error fetching file:', error);
      return null;
    }
  };

  const handleProcess = useCallback(async () => {
    let imageFile = imageSource?.file;
    let audioFile = audioSource?.file;
    const directParams = { mode, prompt: prompt.trim() || undefined };
    const files = [];

    // Image: use File object, or pass HTTP URL directly, or fetch from data/blob URL
    if (!imageFile && imageSource?.preview) {
      if (imageSource.preview.startsWith('http')) {
        directParams.image_url = imageSource.preview;
      } else {
        imageFile = await fetchFileFromUrl(imageSource.preview, imageSource.name, false);
      }
    }
    if (imageFile) files.push({ key: 'image_url', file: imageFile });

    // Audio: same pattern
    if (!audioFile && audioSource?.preview) {
      if (audioSource.preview.startsWith('http')) {
        directParams.audio_url = audioSource.preview;
      } else {
        audioFile = await fetchFileFromUrl(audioSource.preview, audioSource.name, true);
      }
    }
    if (audioFile) files.push({ key: 'audio_url', file: audioFile });

    // Validate we have both inputs
    if (!imageFile && !directParams.image_url) {
      setLocalError('No se pudo obtener la imagen. Verifica la conexión o sube una imagen.');
      return;
    }
    if (!audioFile && !directParams.audio_url) {
      setLocalError('No se pudo obtener el audio. Verifica la conexión o sube un audio.');
      return;
    }

    setLocalError(null);
    resetExec();

    const result = await processNode({
      resultType: 'kling_avatar',
      files,
      params: directParams,
    });

    if (result?.outputUrl) {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'video',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'video',
        tool: 'klingAvatar',
        filename: `kling_avatar_${Date.now()}`,
        url: result.outputUrl,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, imageSource, audioSource, mode, prompt, updateNodeData, addAsset, processNode, resetExec]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const hasValidImage = imageSource?.preview || imageSource?.file || imageInputData?.hasOutput;
  const hasValidAudio = audioSource?.preview || audioSource?.file || audioInputData?.hasOutput;
  const canProcess = hasValidImage && hasValidAudio && !isProcessing;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([inputNeed('image', hasValidImage, { connected: !!imageEdge }), inputNeed('audio', hasValidAudio, { connected: !!audioEdge })]);

  return (
    <div className={`node-container fabric-node kling-avatar-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="kling-avatar-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="klingavatar-reconfigure-notice"
        />
      )}
      {/* Input Handles - IDs must match NODE_TYPE_CONFIG */}
      <Handle type="target" position={Position.Left} id="inputImage" className="node-handle input-handle fabric-handle-image" style={{ top: '30%' }} />
      <Handle type="target" position={Position.Left} id="inputAudio" className="node-handle input-handle fabric-handle-audio" style={{ top: '55%' }} />

      {/* Header */}
      <div className="node-header fabric-header kling-header">
        <div className="node-header-left">
          <Star size={16} />
          <span>Avatar V2 Kling</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content fabric-content">
        {/* Processing State */}
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>Creando avatar Kling...</span>
            </div>
            <p className="node-processing-hint">Generando avatar ({mode === 'pro' ? 'Pro' : 'Standard'})</p>
          </div>
        ) : !showForm ? (
          <div className="node-result">
            <video
              src={data.outputUrl}
              controls
              className="node-result-video clickable"
              onClick={() => openVideoPreview(data.outputUrl, 'Avatar V2 Kling')}
            />
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="klingavatar-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="klingavatar-reconfigure-btn" />
              <button className="node-mini-preview-btn" onClick={() => openVideoPreview(data.outputUrl, 'Avatar V2 Kling')}>
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `kling_avatar_${Date.now()}.mp4`); }}>
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="video" label="klingavatar" testIdPrefix="klingavatar-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Avatar Kling creado</span>
            </div>
          </div>
        ) : (
          <>
            {/* Input Files Grid */}
            <div className="fabric-inputs-grid">
              {/* Image Input */}
              <div className="fabric-input-slot">
                <span className="fabric-input-label">
                  <Image size={10} /> Imagen
                  {imageEdge && <Link2 size={10} className="fabric-connected-icon" />}
                </span>
                {imageSource?.preview ? (
                  <div className="fabric-preview-box image">
                    <img src={imageSource.preview} alt="Source" />
                    {!imageSource.fromConnection && (
                      <button className="fabric-clear-btn" onClick={() => setImageLocal(null)}>
                        <X size={10} />
                      </button>
                    )}
                    {imageSource.fromConnection && (
                      <div className="fabric-connected-badge"><Link2 size={8} /></div>
                    )}
                  </div>
                ) : imageEdge ? (
                  <div className="fabric-waiting-input"><Link2 size={14} /><span>Esperando...</span></div>
                ) : (
                  <button type="button" className="node-load-btn" onClick={handleSourcePickImage} data-testid="load-btn-kling-image">
                    <Upload size={14} /> Cargar
                  </button>
                )}
              </div>

              {/* Audio Input */}
              <div className="fabric-input-slot">
                <span className="fabric-input-label">
                  <Music size={10} /> Audio
                  {audioEdge && <Link2 size={10} className="fabric-connected-icon" />}
                </span>
                {audioSource?.name ? (
                  <div className="fabric-preview-box audio">
                    <Music size={20} />
                    <span className="fabric-audio-name">{audioSource.name.slice(0, 12)}...</span>
                    {!audioSource.fromConnection && (
                      <button className="fabric-clear-btn" onClick={() => setAudioLocal(null)}>
                        <X size={10} />
                      </button>
                    )}
                    {audioSource.fromConnection && (
                      <div className="fabric-connected-badge"><Link2 size={8} /></div>
                    )}
                  </div>
                ) : audioEdge ? (
                  <div className="fabric-waiting-input"><Link2 size={14} /><span>Esperando...</span></div>
                ) : (
                  <button type="button" className="node-load-btn" onClick={handleSourcePickAudio} data-testid="load-btn-kling-audio">
                    <Upload size={14} /> Cargar
                  </button>
                )}
              </div>
            </div>

            {/* Settings */}
            <div className="fabric-settings">
              {/* Mode */}
              <div className="fabric-setting">
                <label><Eye size={12} /> Modo</label>
                <div className="fabric-options">
                  {['std', 'pro'].map((m) => (
                    <button key={m} className={`fabric-option ${mode === m ? 'active' : ''}`} onClick={() => setMode(m)}>
                      {m === 'std' ? 'Standard' : 'Pro'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prompt */}
              <div className="fabric-setting">
                <label><MessageSquare size={12} /> Prompt (opcional)</label>
                <PromptField
                  value={prompt}
                  onChange={setPrompt}
                  nodeId={id}
                  nodeType="klingAvatar"
                  label="Prompt del avatar"
                  placeholder="ej: a person talking happily"
                  className="kling-prompt-input"
                  rows={2}
                  testId="kling-avatar-prompt"
                />
              </div>
            </div>

            {/* Info text */}
            <p className="fabric-info">Avatar realista con emociones via Kling AI</p>

            {/* Process Button */}
            <button
              className="node-process-btn fabric-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="kling-avatar-generate"
            >
              <Play size={14} /> Generar Avatar V2
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        {/* Error Display */}
        <NodeError error={execError || localError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle type="source" position={Position.Right} id="output" className="node-handle output-handle" isConnectable={!!data.hasOutput} />
    </div>
  );
});

KlingAvatarNode.displayName = 'KlingAvatarNode';
export default KlingAvatarNode;
